import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { DbService } from './db.service';
import { AuthService } from './auth.service';
import { SyncEvent, SyncEventEntity, SyncEventType } from '../models/sync-event.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SyncService {
  private http = inject(HttpClient);
  private db = inject(DbService);
  private auth = inject(AuthService);
  private syncing = false;

  readonly isOnline = signal<boolean>(navigator.onLine);
  readonly pendingCount = signal<number>(0);
  readonly failedCount = signal<number>(0);

  private readonly appFocusSubject = new Subject<void>();
  readonly appFocus$ = this.appFocusSubject.asObservable();

  private readonly syncCompletedSubject = new Subject<{ synced: number; failed: number }>();
  readonly syncCompleted$ = this.syncCompletedSubject.asObservable();

  async loadInitialCounts(): Promise<void> {
    const [pending, failed] = await Promise.all([
      this.db.getPendingSyncEvents(),
      this.db.getFailedSyncEvents(),
    ]);
    this.pendingCount.set(pending.length);
    this.failedCount.set(failed.length);
  }

  async enqueue(
    type: SyncEventType,
    entity: SyncEventEntity,
    entityId: number | undefined,
    payload: unknown
  ): Promise<string> {
    const event: SyncEvent = {
      id: crypto.randomUUID(),
      type,
      entity,
      entityId,
      payload,
      localTimestamp: new Date().toISOString(),
      state: 'pending',
    };
    await this.db.putSyncEvent(event);
    this.pendingCount.update(n => n + 1);
    return event.id;
  }

  async markSynced(id: string): Promise<void> {
    await this.db.updateSyncEventState(id, 'synced');
    this.pendingCount.update(n => Math.max(0, n - 1));
  }

  async markFailed(id: string): Promise<void> {
    await this.db.updateSyncEventState(id, 'failed');
    this.pendingCount.update(n => Math.max(0, n - 1));
    this.failedCount.update(n => n + 1);
  }

  async sync(): Promise<void> {
    if (this.syncing || !navigator.onLine) return;
    this.syncing = true;
    let synced = 0;
    let failed = 0;
    try {
      const pending = await this.db.getPendingSyncEvents();
      for (const event of pending) {
        const failedBefore = this.failedCount();
        await this.processEvent(event);
        if (this.failedCount() > failedBefore) {
          failed++;
        } else {
          synced++;
        }
      }
      if (pending.length > 0) {
        this.syncCompletedSubject.next({ synced, failed });
      }
    } finally {
      this.syncing = false;
    }
  }

  async retryFailed(): Promise<void> {
    const count = this.failedCount();
    if (count === 0) return;
    await this.db.resetFailedSyncEvents();
    this.pendingCount.update(n => n + count);
    this.failedCount.set(0);
    await this.sync();
  }

  async getFailedEvents(): Promise<SyncEvent[]> {
    return this.db.getFailedSyncEvents();
  }

  async discardEvent(id: string): Promise<void> {
    await this.db.deleteSyncEvent(id);
    this.failedCount.update(n => Math.max(0, n - 1));
  }

  private async processEvent(event: SyncEvent): Promise<void> {
    const url = this.resolveUrl(event);
    if (!url) {
      await this.markFailed(event.id);
      return;
    }
    try {
      switch (event.type) {
        case 'create':
          await firstValueFrom(this.http.post(url, event.payload));
          break;
        case 'update':
          await firstValueFrom(this.http.put(url, event.payload));
          break;
        case 'delete':
          await firstValueFrom(this.http.delete(url));
          break;
      }
      await this.markSynced(event.id);
    } catch (err) {
      // 404: resource no longer exists (deleted elsewhere) — discard, no retry
      // 409: conflict on create (already synced) — discard, no retry
      if (err instanceof HttpErrorResponse && (err.status === 404 || err.status === 409)) {
        await this.markSynced(event.id);
        return;
      }
      await this.markFailed(event.id);
    }
  }

  private resolveUrl(event: SyncEvent): string | null {
    const base = environment.apiUrl;
    const id = event.entityId;
    switch (event.entity) {
      case 'project':
        return id ? `${base}/projects/${id}` : `${base}/projects`;
      case 'task':
        if (event.type === 'create') {
          const projectId = (event.payload as Record<string, unknown>)?.['projectId'];
          return projectId ? `${base}/projects/${projectId}/tasks` : null;
        }
        return id ? `${base}/tasks/${id}` : null;
      case 'time-entry':
        return id ? `${base}/time-entries/${id}` : `${base}/time-entries`;
      case 'user':
        return `${base}/users/me`;
      default:
        return null;
    }
  }

  listenForReconnect(): void {
    window.addEventListener('online', () => { this.isOnline.set(true); this.sync(); });
    window.addEventListener('offline', () => { this.isOnline.set(false); });
  }

  private async checkSession(): Promise<boolean> {
    const token = await this.auth.getToken();
    if (token && this.auth.isTokenValid(token)) return true;
    const refreshed = await this.auth.refresh();
    if (refreshed) return true;
    await this.auth.logout();
    return false;
  }

  listenForAppFocus(): void {
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState !== 'visible') return;
      const authenticated = await this.checkSession();
      if (!authenticated) return;
      this.sync().catch(() => {});
      this.appFocusSubject.next();
    });
  }
}
